import axios from 'axios';

import { useAppStore } from '@src/store';
import { useAuthStore } from '@src/store/modules/auth.store';
import { devError } from '@src/utils/environment';

interface SessionData {
	ssId: string;
	schedJWT: string;
	organizationId: string;
	status: string;
	expiredDtUtc: string;
	createdAt: number;
	locationId?: string | null;
	// Timeslot hold information
	holdStartTime?: string;
	holdExpirationTime?: string;
	holdActive?: boolean;
	selectedSlot?: string;
}

interface SessionResponse {
	ssId: string;
	jwt: string;
	organizationId: string;
	status: string;
	expiredDtUtc: string;
}

/**
 * Session Manager utility to handle session persistence across page loads
 * - Keeps sessions for all locations until they expire
 * - Only refreshes sessions when they are no longer valid
 * - Uses sessionStorage to persist data between page loads
 */
export const SessionManager = {
	/**
	 * Storage key for sessions in sessionStorage
	 */
	STORAGE_KEY: 'wellnow_sessions',

	/**
	 * Get all stored sessions
	 */
	getAllSessions(): Record<string, SessionData> {
		if (typeof window === 'undefined') return {};

		const sessionsJson = sessionStorage.getItem(this.STORAGE_KEY);
		if (!sessionsJson) return {};

		try {
			return JSON.parse(sessionsJson);
		} catch (error) {
			devError('Error parsing sessions from sessionStorage', error);
			return {};
		}
	},

	/**
	 * Save sessions to sessionStorage
	 */
	saveSessions(sessions: Record<string, SessionData>): void {
		if (typeof window === 'undefined') return;
		sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
	},

	/**
	 * Get a session by location ID
	 * @param locationId - Optional location ID
	 * @returns The session data if found and valid, null otherwise
	 */
	getSession(locationId?: string | null): SessionData | null {
		const sessions = this.getAllSessions();

		// If locationId is provided, try to get that specific session
		if (locationId && sessions[locationId]) {
			const session = sessions[locationId];
			if (this.isSessionValid(session)) {
				return session;
			}
			// If session is invalid, remove it
			this.removeSession(locationId);
			return null;
		}

		// If no locationId is provided or the specific session wasn't found,
		// try to find any valid session
		for (const [id, session] of Object.entries(sessions)) {
			if (this.isSessionValid(session)) {
				return session;
			}
			// Remove invalid session
			this.removeSession(id);
		}

		return null;
	},

	/**
	 * Check if a session is valid (not expired)
	 */
	isSessionValid(session: SessionData): boolean {
		if (!session || !session.expiredDtUtc) return false;

		try {
			const expireDate = new Date(session.expiredDtUtc);
			const now = new Date();

			// Session is valid if expiration date is in the future
			return expireDate > now;
		} catch (error) {
			devError('Error checking session validity', error);
			return false;
		}
	},

	/**
	 * Store a session in sessionStorage
	 * @param sessionData - Session data to store
	 * @param locationId - Optional location ID
	 * @param updateGlobalState - Whether to update the global auth store (default: true)
	 */
	storeSession(sessionData: SessionData, locationId?: string | null, updateGlobalState = true): void {
		const sessions = this.getAllSessions();

		const key = locationId || 'default';
		sessions[key] = {
			...sessionData,
			createdAt: Date.now(),
			locationId,
		};

		this.saveSessions(sessions);

		// Only update the auth store if requested
		// This prevents location-specific sessions from overwriting the main session
		if (updateGlobalState) {
			useAuthStore.getState().setSession({
				schedJWT: sessionData.schedJWT,
				ssId: sessionData.ssId,
			});
		}
	},

	/**
	 * Remove a session from storage
	 */
	removeSession(locationId: string | null): void {
		if (!locationId) return;

		const sessions = this.getAllSessions();
		if (sessions[locationId]) {
			delete sessions[locationId];
			this.saveSessions(sessions);
		}
	},

	/**
	 * Clear all sessions from storage
	 */
	clearAllSessions(): void {
		if (typeof window === 'undefined') return;
		sessionStorage.removeItem(this.STORAGE_KEY);
		useAuthStore.getState().clearSession();
	},

	/**
	 * Create a new session or get an existing valid one
	 * @param accountId - Account ID
	 * @param orgJWT - Organization JWT
	 * @param locationId - Optional location ID
	 * @returns Promise resolving to session data
	 */
	async getOrCreateSession(
		accountId: string | null,
		orgJWT: string,
		locationId?: string | null
	): Promise<SessionData> {
		// First check if we have a valid session
		const existingSession = this.getSession(locationId);
		if (existingSession) {
			return existingSession;
		}

		if (!accountId) {
			throw new Error('accountId is required to create a new session');
		}

		// If no valid session exists, create a new one
		try {
			const response = await axios.post(`${process.env.APP_PATH}/api/session/`, {
				accountId,
				orgJWT,
				workflowType: useAppStore.getState().flowType,
			});

			const { schedJWT, ssId, organizationId, status, expiredDtUtc } = response.data;

			// If the API returns the expiration date, use it directly
			let expirationDate = expiredDtUtc;

			// If not, get the full session data
			if (!expirationDate) {
				const fullSessionData = await this.getFullSessionData(ssId, schedJWT);
				expirationDate = fullSessionData.expiredDtUtc;
			}

			const sessionData: SessionData = {
				ssId,
				schedJWT,
				organizationId,
				status,
				expiredDtUtc: expirationDate,
				createdAt: Date.now(),
				locationId,
			};

			// Store the new session
			this.storeSession(sessionData, locationId);

			return sessionData;
		} catch (error) {
			devError('Error creating new session', error);
			throw error;
		}
	},

	/**
	 * Get full session data including expiration date
	 * This makes a direct API call to get session details
	 */
	async getFullSessionData(ssId: string, schedJWT: string): Promise<SessionResponse> {
		try {
			const response = await axios.get(`${process.env.BASE_API_URL}/v1/scheduler/schedule/${ssId}`, {
				headers: {
					Authorization: `Bearer ${schedJWT}`,
					'Content-Type': 'application/json',
				},
			});

			return response.data;
		} catch (error) {
			devError('Error getting full session data', error);
			throw error;
		}
	},

	/**
	 * Mark a session as used after booking
	 * This will remove the session from storage
	 */
	markSessionAsUsed(locationId?: string | null | undefined): void {
		if (locationId) {
			this.removeSession(locationId);
		} else {
			// If no locationId is provided, clear all sessions
			this.clearAllSessions();
		}
	},

	/**
	 * Update an existing session with new data
	 * @param locationId - Location ID of the session to update
	 * @param updatedData - Partial session data to merge with existing session
	 */
	updateSession(locationId: string, updatedData: Partial<SessionData>): void {
		if (!locationId) return;

		const sessions = this.getAllSessions();
		if (sessions[locationId]) {
			// Merge the updated data with existing session
			sessions[locationId] = {
				...sessions[locationId],
				...updatedData,
			};
			this.saveSessions(sessions);
		}
	},

	/**
	 * Check if a session has an active timeslot hold
	 * @param locationId - Location ID of the session to check
	 * @returns Whether the session has an active hold that hasn't expired
	 */
	hasActiveHold(locationId: string): boolean {
		if (!locationId) return false;

		const sessions = this.getAllSessions();
		if (!sessions[locationId]) return false;

		const session = sessions[locationId];
		if (!session.holdActive || !session.holdExpirationTime) return false;

		// Check if hold hasn't expired
		const now = new Date();
		const expirationTime = new Date(session.holdExpirationTime);

		return now < expirationTime;
	},

	/**
	 * Get the remaining time in seconds for a timeslot hold
	 * @param locationId - Location ID of the session to check
	 * @returns Remaining seconds, or 0 if no active hold
	 */
	getHoldRemainingSeconds(locationId: string): number {
		if (!locationId) return 0;

		const sessions = this.getAllSessions();
		if (!sessions[locationId]) return 0;

		const session = sessions[locationId];
		if (!session.holdActive || !session.holdExpirationTime) return 0;

		const now = new Date().getTime();
		const expirationTime = new Date(session.holdExpirationTime).getTime();
		const remainingMs = expirationTime - now;

		return Math.max(0, Math.floor(remainingMs / 1000));
	},

	/**
	 * Release a timeslot hold
	 * @param locationId - Location ID of the session to update
	 */
	releaseHold(locationId: string): void {
		if (!locationId) return;

		this.updateSession(locationId, {
			holdActive: false,
		});
	},
};
